import React, { useCallback, useEffect, useState } from 'react';
import io from 'socket.io-client';
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Typography,
  Alert,
} from '@mui/joy';
import {
  messagesCredentialsApi,
  MessagesCredentialStatus,
  CreateCredentialRequest,
} from '../api/messages-credentials.api';
import { whatsappApi } from '../api/whatsapp.api';
import { getStoredAccessToken } from '../api/http';
import { useAuth } from '../auth/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function isWebCredential(c: MessagesCredentialStatus): boolean {
  if (c.credentialType === 'whatsapp_web') {
    return true;
  }
  return (
    c.provider === 'whatsapp_web' ||
    c.provider === 'whatsapp_web_js' ||
    c.provider === 'baileys'
  );
}

function shouldCallWebStart(status: MessagesCredentialStatus['status']): boolean {
  return status === 'disconnected' || status === 'error';
}

function formatCredentialStatusLabel(status: MessagesCredentialStatus['status']): string {
  const labels: Record<MessagesCredentialStatus['status'], string> = {
    connected: 'Conectado',
    disconnected: 'Desconectado',
    connecting: 'Conectando',
    error: 'Fallido',
  };
  return labels[status] ?? status;
}

export const ConnectionsPanel: React.FC = () => {
  const [credentials, setCredentials] = useState<MessagesCredentialStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMetaModal, setOpenMetaModal] = useState(false);
  const [metaFormData, setMetaFormData] = useState<Partial<CreateCredentialRequest>>({
    credentialType: 'meta',
    provider: 'meta',
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [webLiveQr, setWebLiveQr] = useState<string | null>(null);
  const [webStarting, setWebStarting] = useState(false);

  const loadCredentials = useCallback(async () => {
    try {
      setLoading(true);
      const data = await messagesCredentialsApi.getStatus();
      setCredentials(data);
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCredentials();
  }, [loadCredentials]);

  useEffect(() => {
    const t = window.setInterval(async () => {
      try {
        const st = await whatsappApi.getWebState();
        setWebLiveQr((prev) => {
          if (st.qr) {
            return st.qr;
          }
          if (!st.clientActive) {
            return null;
          }
          return prev;
        });
      } catch {
        /* ignore */
      }
    }, 2500);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    const token = getStoredAccessToken();
    const socket = io(`${SOCKET_URL}/chat`, {
      auth: { token: token ?? '' },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      withCredentials: true,
    });
    const onWhatsappWebConnection = (payload: {
      phase?: string;
      qr: string | null;
    }) => {
      if (payload.phase === 'authenticated' || payload.phase === 'ready') {
        setWebLiveQr(null);
      } else {
        setWebLiveQr(payload.qr);
      }
      void loadCredentials();
    };
    socket.on('whatsappWebConnection', onWhatsappWebConnection);
    return () => {
      socket.off('whatsappWebConnection', onWhatsappWebConnection);
      socket.disconnect();
    };
  }, [loadCredentials]);

  const handleMetaFormChange = (field: string, value: string) => {
    setMetaFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValidationError(null);
  };

  const handleSaveMeta = async () => {
    try {
      setSubmitting(true);
      setValidationError(null);

      if (!metaFormData.metaAccessToken || !metaFormData.metaPhoneNumberId) {
        setValidationError('Token y Phone Number ID son requeridos');
        return;
      }

      const validation = await messagesCredentialsApi.validateNumber({
        credentialType: 'meta',
        provider: 'meta',
        businessPhoneE164: metaFormData.metaPhoneNumberId,
      });

      if (!validation.allowed) {
        setValidationError(validation.reason || 'No se puede configurar esta credencial');
        return;
      }

      await messagesCredentialsApi.createCredential({
        credentialType: 'meta',
        provider: 'meta',
        businessPhoneE164: metaFormData.metaPhoneNumberId,
        metaAccessToken: metaFormData.metaAccessToken,
        metaPhoneNumberId: metaFormData.metaPhoneNumberId,
        metaVerifyToken: metaFormData.metaVerifyToken || 'secret',
        metaGraphVersion: metaFormData.metaGraphVersion || 'v20.0',
      });

      setOpenMetaModal(false);
      setMetaFormData({ credentialType: 'meta', provider: 'meta' });
      await loadCredentials();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error al guardar la credencial';
      setValidationError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'connecting':
        return <HourglassTopIcon sx={{ color: 'warning.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'danger.main' }} />;
      default:
        return <InfoIcon sx={{ color: 'neutral.main' }} />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const metaCredential = credentials.find(
    (c) => c.credentialType === 'meta' || (!c.credentialType && c.provider === 'meta'),
  );
  const webCredential = credentials.find(isWebCredential);

  const handleEnsureAndStartWeb = async () => {
    try {
      setWebStarting(true);
      await whatsappApi.ensureWebCredential();
      const list = await messagesCredentialsApi.getStatus();
      setCredentials(list);
      const web = list.find(isWebCredential);
      if (web && shouldCallWebStart(web.status)) {
        await whatsappApi.startWeb();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setWebStarting(false);
    }
  };

  const handleLogoutWeb = async () => {
    try {
      setWebStarting(true);
      await whatsappApi.logoutWeb();
      setWebLiveQr(null);
      await loadCredentials();
    } catch (e) {
      console.error(e);
    } finally {
      setWebStarting(false);
    }
  };

  return (
    <Sheet sx={{ p: 3 }}>
      <Stack spacing={3}>
        <div>
          <Typography level="h2">Conexiones de WhatsApp</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5 }}>
            Administra tus conexiones de Meta Business API y WhatsApp Web
          </Typography>
        </div>

        {loading ? (
          <Typography level="body-sm">Cargando conexiones...</Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>
              <Card variant="outlined">
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography level="title-md">Meta Business API</Typography>
                    {metaCredential && getStatusIcon(metaCredential.status)}
                  </Box>

                  {metaCredential ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography level="body-sm">Estado:</Typography>
                        <Chip
                          variant="solid"
                          color={getStatusColor(metaCredential.status)}
                          size="sm"
                        >
                          {formatCredentialStatusLabel(metaCredential.status)}
                        </Chip>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography level="body-sm">Línea:</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                          {metaCredential.businessPhoneE164}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography level="body-sm">Token:</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                          {metaCredential.metaTokenConfigured ? '✓ Configurado' : '✗ No configurado'}
                        </Typography>
                      </Box>

                      {metaCredential.lastError && (
                        <Alert color="danger" startDecorator={<ErrorIcon />}>
                          {metaCredential.lastError}
                        </Alert>
                      )}

                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setOpenMetaModal(true)}
                      >
                        Editar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                        No hay conexión Meta configurada
                      </Typography>
                      <Button
                        variant="solid"
                        color="primary"
                        onClick={() => setOpenMetaModal(true)}
                      >
                        Configurar Meta
                      </Button>
                    </>
                  )}
                </Stack>
              </Card>
            </Grid>

            <Grid xs={12} sm={6}>
              <Card variant="outlined">
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography level="title-md">WhatsApp Web (Lectura)</Typography>
                    {webCredential && getStatusIcon(webCredential.status)}
                  </Box>

                  {webCredential ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography level="body-sm">Estado:</Typography>
                        <Chip
                          variant="solid"
                          color={getStatusColor(webCredential.status)}
                          size="sm"
                        >
                          {formatCredentialStatusLabel(webCredential.status)}
                        </Chip>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography level="body-sm">Línea:</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                          {webCredential.businessPhoneE164 === '0' || webCredential.businessPhoneE164 === 'pending'
                            ? '—'
                            : webCredential.businessPhoneE164}
                        </Typography>
                      </Box>

                      {webLiveQr && webCredential.status !== 'connected' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                          <Typography level="body-xs">Escaneá el QR con WhatsApp en el teléfono</Typography>
                          <QRCodeSVG value={webLiveQr} size={200} level="M" />
                        </Box>
                      )}

                      <Alert color="neutral" startDecorator={<InfoIcon />}>
                        Modo solo lectura. Los mensajes no se pueden enviar desde esta conexión.
                      </Alert>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          loading={webStarting}
                          variant="solid"
                          color="warning"
                          onClick={handleEnsureAndStartWeb}
                          disabled={
                            webStarting ||
                            webCredential.status === 'connecting' ||
                            webCredential.status === 'connected'
                          }
                        >
                          {webCredential.status === 'connected'
                            ? 'Conectado'
                            : webCredential.status === 'connecting'
                              ? 'Conectando…'
                              : 'Conectar / QR'}
                        </Button>
                        <Button
                          loading={webStarting}
                          variant="outlined"
                          color="neutral"
                          onClick={handleLogoutWeb}
                        >
                          Cerrar sesión Web
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                        Creá la credencial Web y conectá con QR
                      </Typography>
                      <Button
                        loading={webStarting}
                        variant="solid"
                        color="warning"
                        onClick={async () => {
                          try {
                            setWebStarting(true);
                            await whatsappApi.ensureWebCredential();
                            const list = await messagesCredentialsApi.getStatus();
                            setCredentials(list);
                            const web = list.find(isWebCredential);
                            if (web && shouldCallWebStart(web.status)) {
                              await whatsappApi.startWeb();
                            }
                          } finally {
                            setWebStarting(false);
                          }
                        }}
                      >
                        Crear credencial WhatsApp Web
                      </Button>
                    </>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}

        <Modal
          open={openMetaModal}
          onClose={() => {
            setOpenMetaModal(false);
            setValidationError(null);
          }}
        >
          <ModalDialog>
            <ModalClose variant="plain" sx={{ m: 1 }} />
            <Typography level="h2">Configurar Meta Business API</Typography>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSaveMeta();
              }}
            >
              <Stack spacing={2}>
                {validationError && (
                  <Alert color="danger" startDecorator={<ErrorIcon />}>
                    {validationError}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Access Token</FormLabel>
                  <Input
                    type="password"
                    value={metaFormData.metaAccessToken || ''}
                    onChange={(e) => handleMetaFormChange('metaAccessToken', e.target.value)}
                    placeholder="EAAM8Qy9d0o8BRJJ..."
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Phone Number ID</FormLabel>
                  <Input
                    value={metaFormData.metaPhoneNumberId || ''}
                    onChange={(e) => handleMetaFormChange('metaPhoneNumberId', e.target.value)}
                    placeholder="995084120363109"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Verify Token (opcional)</FormLabel>
                  <Input
                    type="password"
                    value={metaFormData.metaVerifyToken || ''}
                    onChange={(e) => handleMetaFormChange('metaVerifyToken', e.target.value)}
                    placeholder="secret"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Graph Version (opcional)</FormLabel>
                  <Input
                    value={metaFormData.metaGraphVersion || 'v20.0'}
                    onChange={(e) => handleMetaFormChange('metaGraphVersion', e.target.value)}
                    placeholder="v20.0"
                  />
                </FormControl>

                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  <Button loading={submitting} type="submit" variant="solid" color="primary">
                    Guardar
                  </Button>
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={() => {
                      setOpenMetaModal(false);
                      setValidationError(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>
      </Stack>
    </Sheet>
  );
};
